import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import Fade from '@mui/material/Fade';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { alpha } from '@mui/material/styles';
import Alert from '@mui/material/Alert';
import ErrorIcon from '@mui/icons-material/Error';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from '@mui/material/styles';

/**
 * TaxPrerequisitesDialog - Reusable dialog component for displaying tax calculation prerequisites
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {Function} props.onClose - Callback when dialog is closed
 * @param {Object} props.prerequisites - Prerequisites data from the API
 */
const TaxPrerequisitesDialog = ({ open, onClose, prerequisites }) => {
  const theme = useTheme();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        elevation: 6,
        sx: { borderRadius: 2 }
      }}
      TransitionComponent={Fade}
      transitionDuration={300}
    >
      <DialogTitle sx={{
        bgcolor: theme.palette.error.light,
        color: theme.palette.error.contrastText,
        display: 'flex',
        alignItems: 'center',
        gap: 1
      }}>
        <ErrorIcon />
        Unable to Calculate Taxes
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        {prerequisites && (
          <>
            <Paper elevation={1} sx={{ p: 2, mb: 3, bgcolor: theme.palette.background.default, borderRadius: 2 }}>
              <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Order:
                </Typography>
                <Typography variant="subtitle1">{prerequisites.order_identifier}</Typography>
                <Chip label={prerequisites.order_type} size="small" color="primary" />
              </Stack>

              <Typography variant="body1" gutterBottom>
                {prerequisites.message}
              </Typography>
            </Paper>

            {prerequisites.missing_requirements && prerequisites.missing_requirements.length > 0 && (
              <Box>
                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ReportProblemIcon color="warning" />
                  Missing Requirements
                </Typography>

                {prerequisites.missing_requirements.map((req, index) => (
                  <Paper
                    key={index}
                    elevation={2}
                    sx={{
                      mb: 2,
                      p: 2,
                      bgcolor: alpha(theme.palette.warning.light, 0.2),
                      borderLeft: `4px solid ${theme.palette.warning.main}`,
                      borderRadius: 1
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                      {req.message}
                    </Typography>

                    <Box sx={{ mt: 1, mb: 2, p: 1, bgcolor: alpha(theme.palette.background.paper, 0.5), borderRadius: 1 }}>
                      <Typography variant="body2" fontWeight="medium" color="text.secondary">
                        Required Action:
                      </Typography>
                      <Typography variant="body2">
                        {req.user_action || "Please fill in the required information."}
                      </Typography>
                    </Box>

                    {req.missing_fields && req.missing_fields.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" fontWeight="medium">Missing Information:</Typography>
                        <List dense disablePadding sx={{ ml: 1 }}>
                          {req.missing_fields.map((field, idx) => (
                            <ListItem key={idx} disableGutters>
                              <ListItemIcon sx={{ minWidth: 28 }}>•</ListItemIcon>
                              <ListItemText
                                primary={req.missing_field_labels && req.missing_field_labels[idx] || field}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    )}

                    {req.products_with_issues && req.products_with_issues.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" fontWeight="medium">Products with Issues:</Typography>
                        <List dense disablePadding>
                          {req.products_with_issues.map((product, idx) => (
                            <ListItem
                              key={idx}
                              sx={{
                                flexDirection: 'column',
                                alignItems: 'flex-start',
                                pl: 1,
                                mb: 1,
                                bgcolor: alpha(theme.palette.background.paper, 0.5),
                                borderRadius: 1
                              }}
                            >
                              <ListItemText
                                primary={product.product_name}
                                primaryTypographyProps={{ fontWeight: 'medium' }}
                              />
                              <Box pl={2} width="100%">
                                <Typography variant="body2" color="text.secondary" fontWeight="medium" gutterBottom>
                                  Missing Attributes:
                                </Typography>
                                <List dense disablePadding>
                                  {product.missing_attributes.map((attr, attrIdx) => (
                                    <ListItem key={attrIdx} dense disableGutters sx={{ py: 0.5 }}>
                                      <ListItemIcon sx={{ minWidth: 28 }}>•</ListItemIcon>
                                      <ListItemText
                                        primary={product.missing_attribute_labels &&
                                          product.missing_attribute_labels[attrIdx] || attr}
                                      />
                                    </ListItem>
                                  ))}
                                </List>
                              </Box>
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    )}
                  </Paper>
                ))}
              </Box>
            )}

            {prerequisites.warnings && prerequisites.warnings.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" color="info.main" gutterBottom>
                  Warnings
                </Typography>
                {prerequisites.warnings.map((warning, idx) => (
                  <Alert key={idx} severity="info" sx={{ mb: 1 }}>
                    {warning.message}
                  </Alert>
                ))}
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
        <Button
          onClick={onClose}
          variant="contained"
          color="primary"
          startIcon={<CloseIcon />}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TaxPrerequisitesDialog;